import React, { useEffect, useState, useCallback } from 'react'
import { useNavigate, useParams, Link, useLocation } from 'react-router-dom'
import axios from 'axios'
import { ArrowLeftIcon } from '@heroicons/react/24/solid'
import { useAuthStatus, getAccessTokenFromStorage } from '../../utils'
import offerStyles from '../offers/manage/OfferManagementDetail.module.scss'
import localStyles from './JobPostPublicDetailView.module.scss'
import JobPostPublicContractSection from './JobPostPublicContractSection'

const API_ENDPOINT = process.env.REACT_APP_API_ENDPOINT

const APPLICANTS_ANCHOR = 'job-post-applicants'

const JobPostPublicDetailView = () => {
	const { postId } = useParams()
	const navigate = useNavigate()
	const location = useLocation()
	const [auth] = useAuthStatus()
	const [detail, setDetail] = useState(null)
	const [loading, setLoading] = useState(true)
	const [err, setErr] = useState(null)
	const [message, setMessage] = useState('')
	const [submitting, setSubmitting] = useState(false)
	const [applicants, setApplicants] = useState([])
	const [applicantsLoading, setApplicantsLoading] = useState(false)

	const authUserId =
		auth?.state === 'loaded' && auth?.isAuthentication && auth?.user?.id ? auth.user.id : null

	const fetchApplicants = useCallback(async () => {
		if (!postId || !authUserId) return
		const token = auth?.token ?? getAccessTokenFromStorage()
		if (!token) return
		setApplicantsLoading(true)
		try {
			const aRes = await axios.get(`${API_ENDPOINT}/job-post/${postId}/applicants`, {
				headers: { Authorization: 'Bearer ' + token },
			})
			setApplicants(Array.isArray(aRes.data) ? aRes.data : [])
		} catch {
			setApplicants([])
		} finally {
			setApplicantsLoading(false)
		}
	}, [postId, authUserId, auth?.token])

	useEffect(() => {
		let cancelled = false
		const run = async () => {
			try {
				setLoading(true)
				const res = await axios.get(`${API_ENDPOINT}/job-post/${postId}`)
				if (!cancelled) setDetail(res.data)
			} catch (e) {
				if (!cancelled) setErr(e.message)
			} finally {
				if (!cancelled) setLoading(false)
			}
		}
		if (postId) run()
		return () => {
			cancelled = true
		}
	}, [postId])

	const isOwner =
		Boolean(detail?.writerUserId) && Boolean(authUserId) && detail.writerUserId === authUserId

	useEffect(() => {
		if (isOwner) fetchApplicants()
	}, [isOwner, fetchApplicants])

	useEffect(() => {
		if (!detail || loading) return
		const hash = location.hash?.replace(/^#/, '')
		if (hash === APPLICANTS_ANCHOR) {
			const t = requestAnimationFrame(() => {
				document.getElementById(APPLICANTS_ANCHOR)?.scrollIntoView({
					behavior: 'smooth',
					block: 'start',
				})
			})
			return () => cancelAnimationFrame(t)
		}
	}, [detail, loading, location.hash])

	const onApply = async () => {
		if (!auth?.isAuthentication) {
			navigate('/login')
			return
		}
		if (!message.trim()) {
			alert('지원 메시지를 입력해주세요.')
			return
		}
		try {
			setSubmitting(true)
			const accessToken = auth?.token ?? getAccessTokenFromStorage()
			if (!accessToken) {
				navigate('/login')
				return
			}
			await axios.post(
				`${API_ENDPOINT}/job-post/${postId}/apply`,
				{ message: message.trim() },
				{
					headers: {
						Authorization: 'Bearer ' + accessToken,
					},
				},
			)
			alert('지원이 완료되었습니다. 제안관리에서 확인해 주세요.')
			navigate('/offers', { state: { defaultTab: 'sent' } })
		} catch (e) {
			const msg =
				e.response?.data?.message ||
				(Array.isArray(e.response?.data?.message)
					? e.response.data.message.join(', ')
					: null) ||
				e.message
			alert(msg || '지원에 실패했습니다.')
		} finally {
			setSubmitting(false)
		}
	}

	const goEdit = () => {
		sessionStorage.setItem('editPostItem', JSON.stringify({ id: postId }))
		navigate('/posts/manage/edit')
	}

	const goBack = () => {
		navigate(-1)
	}

	if (loading) {
		return (
			<div className={offerStyles.suggestManagementDetailViewContainer}>
				<div className={localStyles.loadingBox}>불러오는 중…</div>
			</div>
		)
	}
	if (err || !detail) {
		return (
			<div className={offerStyles.suggestManagementDetailViewContainer}>
				<div className={localStyles.errorBox}>{err || '공고를 찾을 수 없습니다.'}</div>
			</div>
		)
	}

	const closed = detail.status === 'closed'
	const offerCreated = (o) => o.createdAt ?? o.created_at

	return (
		<div className={offerStyles.suggestManagementDetailViewContainer}>
			<div
				className={`${offerStyles.backButton} ${localStyles.jobPostBackButton}`}
				onClick={goBack}
				onKeyDown={(e) => {
					if (e.key === 'Enter' || e.key === ' ') {
						e.preventDefault()
						goBack()
					}
				}}
				role="button"
				tabIndex={0}>
				<ArrowLeftIcon className={offerStyles.backButtonIcon} />
				뒤로
			</div>

			{isOwner ? (
				<div className={localStyles.ownerActions}>
					<button type="button" className={localStyles.ownerBtnPrimary} onClick={goEdit}>
						수정하기
					</button>
					<button
						type="button"
						className={localStyles.ownerBtn}
						onClick={() => navigate('/posts/manage')}>
						공고 관리
					</button>
				</div>
			) : null}

			<div className={offerStyles.suggestManagementDetailCardContainer}>
				<div className={offerStyles.suggestManagementDetailCardHeader}>
					<div className={offerStyles.suggestManagementDetailCardHeaderContent}>
						<span className={offerStyles.creationDate}>{detail.createdAt}</span>
						<div className={offerStyles.headerStatus}>
							<span
								className={
									closed
										? `${offerStyles.itemStatus} ${offerStyles.itemStatusRefuse}`
										: offerStyles.itemStatus
								}>
								{closed ? '마감' : '모집중'}
							</span>
						</div>
					</div>
				</div>
				<div className={offerStyles.suggestManagementDetailCardMainContainer}>
					<div className={localStyles.postTitle}>{detail.subject}</div>
					<div className={offerStyles.profileSubInfo}>{detail.writerName}</div>
				</div>
			</div>

			<div className={offerStyles.senderHello} style={{ whiteSpace: 'pre-wrap' }}>
				{detail.content}
			</div>

			<div className={offerStyles.suggestManagementDetailCardContainer}>
				<JobPostPublicContractSection detail={detail} />
			</div>

			{isOwner ? (
				<section id={APPLICANTS_ANCHOR} className={localStyles.applicantsSection}>
					<h2 className={localStyles.applicantsTitle}>공고 지원자</h2>
					{applicantsLoading ? (
						<p className={offerStyles.profileSubInfo}>불러오는 중…</p>
					) : applicants.length === 0 ? (
						<p className={offerStyles.profileSubInfo}>아직 지원한 캐디가 없습니다.</p>
					) : (
						<ul style={{ listStyle: 'none', padding: 0, margin: 0 }}>
							{applicants.map((o) => (
								<li
									key={o.id}
									style={{
										marginBottom: 10,
										paddingBottom: 10,
										borderBottom: '1px solid #ececec',
									}}>
									<Link
										to={`/offers/${o.id}`}
										style={{ color: '#085e4b', fontWeight: 500, fontSize: 14 }}>
										{o.user?.name || '캐디'} ·{' '}
										{offerCreated(o)
											? new Date(offerCreated(o)).toLocaleDateString('ko-KR')
											: ''}
									</Link>
								</li>
							))}
						</ul>
					)}
				</section>
			) : null}

			{closed ? (
				<p className={localStyles.closedNotice}>마감된 공고입니다.</p>
			) : isOwner ? null : (
				<div className={localStyles.applySection}>
					<label className={localStyles.applyLabel} htmlFor="job-post-apply-message">
						지원 메시지
					</label>
					<textarea
						id="job-post-apply-message"
						className={localStyles.applyTextarea}
						rows={4}
						value={message}
						onChange={(e) => setMessage(e.target.value)}
						placeholder="프로님께 전할 메시지를 입력해주세요."
					/>
					<button
						type="button"
						className={localStyles.applyBtn}
						disabled={submitting}
						onClick={onApply}>
						{submitting ? '처리 중…' : '이 공고에 지원하기'}
					</button>
				</div>
			)}

			{!isOwner ? (
				<div className={localStyles.footerLink}>
					<Link to="/offers">제안관리로 이동</Link>
				</div>
			) : null}
		</div>
	)
}

export default JobPostPublicDetailView
